import { Fragment, useCallback, useRef, useState } from 'react';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { HttpResponseList } from 'shell/models/http-response';
import { initialValuesTerritorialAreas } from '..';
import { FormikHelpers, useFormik } from 'formik';
import { FiltersFormTerritorialArea } from './types';
import { transformDataset } from './utils';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { TerritorialArea } from 'shell/models/territorial-area';
import { headCells, validationSchemaToponymyFilter } from './constants';
import { Typography } from '@mui/material';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { TerritorialAreaClient } from 'shell/view-models/territorial-area-client';
import FiltersTerritorialArea from 'system/components/toponymy/territorial-area/filter';
import PmiTitle from 'shell/components/shared/title';
import { CommonSystemToponomyCrumbs } from '../../constants/breadcrumb';
import { transformerList } from './utils';
import { CsvExtraction } from 'shell/components/shared/csv-extraction';
import UpdateCivicModal from 'system/components/toponymy/territorial-area/update-civic-modal';
import { noop } from 'shell/utils/noop';

const TerritorialAreasPage = (): React.ReactElement => {
  const [openTerritorialArea, setOpenTerritorialArea] = useState<boolean>(true);
  const [openUpdateTerritorialAreaModal, setOpenUpdateTerritorialAreaModal] =
    useState<boolean>(false);

  const count = useRef<number>(0);

  const [pagination, setPagination] = useState<{ page: number; rowsPerPage?: number }>({
    page: 0,
    rowsPerPage: 10,
  });

  const [visibleTable, setVisibileTable] = useState<boolean>();

  const [territorialAreasData, getTerritorialAreas] = useHttpFetch<
    HttpResponseList<TerritorialArea>,
    TerritorialAreaClient[]
  >(`${__TOPONYMY_API_URL__}/civico`, {
    onBootstrap: false,
    options: {
      method: 'GET',
      errorCatching: true,
      loading: true,
    },
    transformer: (response) => {
      count.current = response.data.payload?.pageInfo?.totalCount || 0;
      return transformerList(response);
    },
  });

  const onSubmit = useCallback(
    (
      formValues: FiltersFormTerritorialArea,
      formikHelpers?: FormikHelpers<FiltersFormTerritorialArea>
    ) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      handleChangePage(0);
      (
        getTerritorialAreas({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TerritorialArea>, TerritorialAreaClient[]>
      ).then(() => {
        formikHelpers?.resetForm({ values: formValues });
        setVisibileTable(true);
      });
    },
    [getTerritorialAreas]
  );

  const onReset = useCallback(() => {
    setVisibileTable(false);
  }, []);

  const territorialAreas = (territorialAreasData.data as TerritorialAreaClient[]) || [];

  const filterTerritorialAreaForm = useFormik({
    initialValues: initialValuesTerritorialAreas,
    validationSchema: validationSchemaToponymyFilter,
    onSubmit,
    onReset,
  });

  const handleChangePage = useCallback(
    (newPage) => {
      setPagination((oldState) => ({ ...oldState, page: newPage }));
    },
    [setPagination]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      setPagination(newDataset);
    },
    [setPagination]
  );

  return (
    <Fragment>
      <Breadcrumbs
        items={[
          ...CommonSystemToponomyCrumbs,
          {
            name: 'Aggiornamento Stradario e Ambiti Territoriali',
            path: '/sistema/toponomastica/ambiti-territoriali',
          },
        ]}
      />
      <PmiTitle title="Aggiornamento Stradario e Ambiti Territoriali" />
      <div className="pmi-my-3">
        <FiltersTerritorialArea
          filterTerritorialAreaForm={filterTerritorialAreaForm}
          setOpen={setOpenTerritorialArea}
          open={openTerritorialArea}
        />
      </div>

      {openUpdateTerritorialAreaModal ? (
        <UpdateCivicModal
          open={openUpdateTerritorialAreaModal}
          onClose={() => setOpenUpdateTerritorialAreaModal(false)}
          filterForm={filterTerritorialAreaForm.values}
          onConfirm={() => {
            setOpenUpdateTerritorialAreaModal(false);
            onSubmit(filterTerritorialAreaForm.values);
          }}
        />
      ) : undefined}
      {visibleTable && territorialAreasData.data ? (
        territorialAreas?.length > 0 ? (
          <>
            <div className="pmi-d-flex pmi-align-items-center pmi-justify-content-between">
              <CsvExtraction
                label="Aggiorna stradario"
                disabled={territorialAreas.length === 0 || filterTerritorialAreaForm.dirty}
                onClick={() => {
                  setOpenUpdateTerritorialAreaModal(true);
                }}
              />
              <Typography>
                Totale civici: <b>{count.current}</b>
              </Typography>
            </div>
            <Table
              onSort={noop}
              head={headCells}
              orders={filterTerritorialAreaForm.values.orders || {}}
              page={pagination.page || 0}
              rowsPerPage={pagination.rowsPerPage}
              body={territorialAreas}
              count={count.current || 0}
              noSort
              hideButtons
              onRowsPerPageChange={handleChangeRowsPerPage}
              onPageChange={handleChangePage}
              ariaLabel="Lista Aree Territoriali"
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

TerritorialAreasPage.displayName = 'Territorial areas page component';

export default TerritorialAreasPage;
